import { useCallback, useEffect, useMemo } from 'react';
import { debounce } from 'lodash-es';
import { MS_IN_S } from '@/constants/date.constants';
import { refetchRouteLogistics } from '@/queries/route-planner/utils';
import { invalidateCaseItemQueries } from '@/queries/case';
import { invalidateQuoteQueries } from '@/queries/quotes';
import { getGroundTripsList } from '@/queries/drive-service';
import { useCaseEventsSubscription } from '@/hooks/useCaseEventsSubscription';
import { CaseEventType, CaseGroundTripEventType, CaseRouteEventType, WsCaseEventMessageContent } from '@/ws';
import { createBufferedCollector } from '@/ws/utils';

const RESOURCE_LOGISTICS_RELATED_EVENTS = [CaseEventType.CaseResourceAssigned, CaseEventType.CaseResourceReleased];
const QUOTE_RELATED_EVENTS = [
  CaseEventType.CaseQuoteCommentSet,
  CaseEventType.CaseQuoteCreated,
  CaseEventType.CaseQuoteRejectedInternally,
  CaseEventType.CaseQuoteApprovedInternally,
  CaseEventType.CaseQuoteRecalled,
  CaseEventType.CaseQuoteSentToTxCenter,
  CaseEventType.CaseQuoteRejectedByTxCenter,
  CaseEventType.CaseQuoteApprovedByTxCenter,
  CaseEventType.CaseQuoteApprovedTxCenterProvider,
];
const GROUND_TRIPS_REFETCH_INTERVAL = 10 * MS_IN_S;
const CASE_UPDATE_DEBOUNCE_INTERVAL = 0.5 * MS_IN_S;

export const useCaseEventsSubHandler = (caseId: string, logisticsId?: string) => {
  const debouncedRefetchLogistics = useMemo(() => debounce(refetchRouteLogistics, CASE_UPDATE_DEBOUNCE_INTERVAL), []);
  const debouncedRefetchCase = useMemo(() => debounce(invalidateCaseItemQueries, CASE_UPDATE_DEBOUNCE_INTERVAL), []);
  const debouncedRefetchQuote = useMemo(() => debounce(invalidateQuoteQueries, CASE_UPDATE_DEBOUNCE_INTERVAL), []);

  const groundTripsCollector = useMemo(() => {
    return createBufferedCollector<string>(async (ids) => {
      await getGroundTripsList(ids);
    }, GROUND_TRIPS_REFETCH_INTERVAL);
  }, []);

  const caseEventSubHandler = useCallback(
    (message: WsCaseEventMessageContent) => {
      if (message.caseId !== caseId) return;

      if (message.eventType === CaseGroundTripEventType.GroundTripUpdated) {
        groundTripsCollector.push(message.groundTripId);
      }

      if (message.eventType === CaseRouteEventType.RouteChanged) {
        debouncedRefetchLogistics(message.logisticsId);
      }

      if (RESOURCE_LOGISTICS_RELATED_EVENTS.includes(message.eventType as CaseEventType) && logisticsId) {
        debouncedRefetchLogistics(logisticsId);
      }

      if (QUOTE_RELATED_EVENTS.includes(message.eventType as CaseEventType)) {
        debouncedRefetchQuote();
      }

      debouncedRefetchCase(caseId);
    },
    [caseId, logisticsId, debouncedRefetchCase, debouncedRefetchQuote, groundTripsCollector, debouncedRefetchLogistics],
  );

  useEffect(() => {
    return () => {
      groundTripsCollector.flushNow();
      groundTripsCollector.cancel();
      debouncedRefetchLogistics.flush();
      debouncedRefetchCase.flush();
      debouncedRefetchQuote.flush();
    };
  }, [debouncedRefetchCase, debouncedRefetchQuote, debouncedRefetchLogistics, groundTripsCollector]);

  return useCaseEventsSubscription([caseId], caseEventSubHandler);
};
